import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { IonicModule, ModalController } from '@ionic/angular';
import { KeycloakService } from 'keycloak-angular';
import { KeycloakUserInfo } from 'src/app/services/bruker/bruker.types';
import { CustomerFeedbackService } from 'src/app/services/customer-feedback/customer-feedback.service';
import { ContactUsModalComponent } from 'src/app/tabs/contact-us-modal/contact-us-modal.component';

@Component({
  selector: 'app-feedback-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    IonicModule,
    ContactUsModalComponent
  ],
  template: `
    @if (isLoading) {
    <div>
      <ion-spinner name="crescent"></ion-spinner>
    </div>
    }
    <div class="modalClose">
      <img src="/assets/img/shared/close.svg" (click)="closeModal()" />
    </div>
    <ion-title class="smaller">
      <h1>Gi din tilbakemelding</h1>
    </ion-title>
    @if (!displayThanks && !hasError) {
    <p>Hvordan var opplevelsen din med strømanalysen?</p>
    <div class="m-4">
      <form [formGroup]="feedbackForm" class="form-input-ihk form-container">
        <div class="flex justify-center gap-4 mb-6">
          <ion-button
            (click)="giveRating(true)"
            class="w-full"
            [class.selected]="feedbackForm.get('rating')?.value === true"
          >
            <ion-icon name="thumbs-up-outline" class="mr-2"></ion-icon>
            Bra
          </ion-button>
          <ion-button
            (click)="giveRating(false)"
            class="w-full"
            [class.selected]="feedbackForm.get('rating')?.value === false"
          >
            <ion-icon name="thumbs-down-outline" class="mr-2"></ion-icon>
            Ikke helt bra
          </ion-button>
        </div>
        <div class="mb-6">
          <p>Vi ønsker gjerne å vite mer om din opplevelse</p>
          <ion-textarea
            placeholder="Kommentar (Valgfri)"
            formControlName="comment"
            fill="outline"
            [rows]="3"
          ></ion-textarea>
        </div>
        <div class="mb-6">
          <p>
            Kan vi følge deg opp vedrørende tilbakemeldingen hvis vi har
            spørsmål?
          </p>
          <ion-radio-group
            formControlName="contact_me"
            [allowEmptySelection]="false"
            class="flex justify-center gap-8"
          >
            <ion-radio value="true">Ja</ion-radio>
            <ion-radio value="false">Nei</ion-radio>
          </ion-radio-group>
        </div>
        <ion-button
          class="dark-primary wide"
          (click)="sendFeedback()"
          [disabled]="!feedbackForm.valid || isSending"
          >Send inn @if (isSending) {
          <ion-spinner name="crescent"></ion-spinner>
          }
        </ion-button>
      </form>
    </div>
    }
    <div class="mb-4 mx-4">
      @if (displayThanks) {
      <p>Takk for din tilbakemelding!</p>
      <img src="/assets/img/Cat-in-space.svg" class="w-1/2" />
      } @if (hasError) {
      <p>Beklager, det skjedde en feil.</p>
      }
      <p (click)="closeModal()" class="underline cursor-pointer">
        {{ displayThanks ? 'Lukk' : 'Avbryt' }}
      </p>
    </div>
    <div class="mx-4">
      <p class="text-sm italic">
        Tilbakemeldingen blir benyttet internt av vårt utviklingsteam for å
        forbedre strømanalysen, men vil ikke bli fulgt opp av kundeservice.
      </p>
      <p class="text-sm italic">
        Hvis du ønsker å kontakte kundeservice kan du trykke på knappen under.
        Da får du valget om å sende oss en e-post, ringe oss eller bli ringt opp
        av kundeservice. Du finner også dette valget i sidemenyen i appen.
      </p>
      <ion-button (click)="showContactUs = true"> Kontakt oss </ion-button>
    </div>
    <ion-modal
      #contactUsFeedbackModal
      [isOpen]="showContactUs"
      cssClass="contact-us-modal-content"
      backdropDismiss
      (didDismiss)="showContactUs = false"
    >
      <ng-template>
        <app-contact-us-modal></app-contact-us-modal>
      </ng-template>
    </ion-modal>
  `,
  styleUrl: './feedback-modal.component.scss'
})
export class FeedbackModalComponent {
  @Input() rating: boolean;
  kunde_id: string;
  feedbackForm: FormGroup;

  isLoading = false;
  isSending = false;
  displayThanks = false;
  hasError = false;
  showContactUs = false;

  constructor(
    private _customerFeedbackService: CustomerFeedbackService,
    private _keycloak: KeycloakService,
    private modalCtrl: ModalController
  ) {}

  ngOnInit() {
    this.isLoading = true;
    this._keycloak
      .getKeycloakInstance()
      .loadUserInfo()
      .then((userInfo: KeycloakUserInfo) => {
        this.kunde_id = userInfo.kunde_id;
        this.isLoading = false;
      });

    this.feedbackForm = new FormGroup({
      rating: new FormControl(this.rating, Validators.required),
      comment: new FormControl(''),
      contact_me: new FormControl('true', Validators.required)
    });
  }

  giveRating(rating: boolean) {
    this.feedbackForm.setValue({ ...this.feedbackForm.value, rating });
  }

  sendFeedback() {
    this.isSending = true;
    const feedback = {
      rating: this.rating,
      kunde_id: this.kunde_id,
      comment: this.feedbackForm.get('comment').value,
      contact_me:
        this.feedbackForm.get('contact_me').value === 'true' ? true : false
    };

    this._customerFeedbackService
      .postFeedbackAnalysisFeature(feedback)
      .subscribe({
        next: (_) => {
          this.isSending = false;
          this.displayThanks = true;
        },
        error: (err) => {
          this.isSending = false;
          this.hasError = true;
        }
      });
  }

  closeModal() {
    this.modalCtrl.dismiss();
  }
}
